import { CreateRoundRequest, CreateRoundResponse, Round } from '../models/round';
import { getAuthHeaders } from './auth-headers';

export class HttpError extends Error {
    constructor(
        public readonly status: number,
        message: string
    ) {
        super(message);
        this.name = 'HttpError';
    }
}

class RoundService {
    public async createRound(roundName: string, ownerPlayerId: string, courseId?: string) {
        try {
            const authHeaders = await getAuthHeaders(ownerPlayerId);
            const request: CreateRoundRequest = {
                name: roundName,
                ownerPlayerId,
                ...(courseId ? { courseId } : {}),
            };
            const response = await fetch(`${process.env.REACT_APP_BASE_API_URL!}/rounds`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders,
                },
                body: JSON.stringify(request),
            });
            if (!response.ok) {
                throw new Error(`Failed to create round: ${response.status} ${response.statusText}`);
            }

            return {
                body: (await response.json()) as CreateRoundResponse,
                status: response.status,
            };
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    public async getRound(roundId: string) {
        let url = `${process.env.REACT_APP_BASE_API_URL!}/rounds/${roundId}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error(`Failed to get round: ${response.status} ${response.statusText}`);
            }
            return {
                body: (await response.json()) as Round,
                status: response.status,
            };
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    public async attachPlayerToRound(roundId: string, playerId: string) {
        try {
            const authHeaders = await getAuthHeaders(playerId);
            const response = await fetch(`${process.env.REACT_APP_BASE_API_URL!}/rounds/${roundId}/players`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders,
                },
                body: JSON.stringify({ playerId }),
            });
            if (response.status === 404) {
                throw new HttpError(404, `Player not found`);
            }
            if (!response.ok) {
                throw new Error(`Failed to attach player to round: ${response.status} ${response.statusText}`);
            }
            return {
                body: await response.json(),
            };
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    public async updatePlayerRoundStatus(roundId: string, playerId: string, status: string) {
        try {
            const authHeaders = await getAuthHeaders(playerId);
            const response = await fetch(
                `${process.env.REACT_APP_BASE_API_URL!}/rounds/${roundId}/players/${playerId}/status`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...authHeaders,
                    },
                    body: JSON.stringify({ status }),
                }
            );
            if (!response.ok) {
                throw new Error(`Failed to update player round status: ${response.status} ${response.statusText}`);
            }
            return {
                body: await response.json(),
            };
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    public async recordScore(roundId: string, playerId: string, holeNumber: number, score: number) {
        try {
            const authHeaders = await getAuthHeaders(playerId);
            const response = await fetch(
                `${process.env.REACT_APP_BASE_API_URL!}/rounds/${roundId}/players/${playerId}/scores/${holeNumber}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...authHeaders,
                    },
                    body: JSON.stringify({ score }),
                }
            );
            if (response.status === 401 || response.status === 403) {
                throw new HttpError(response.status, `Score recording denied: ${response.status}`);
            }
            if (!response.ok) {
                throw new Error(`Failed to record score: ${response.status} ${response.statusText}`);
            }
            return {
                body: await response.json(),
            };
        } catch (error) {
            if (error instanceof HttpError) throw error;
            console.log(error);
            throw error;
        }
    }

    public async setHolePar(roundId: string, playerId: string, holeNumber: number, par: number) {
        try {
            const authHeaders = await getAuthHeaders(playerId);
            const response = await fetch(
                `${process.env.REACT_APP_BASE_API_URL!}/rounds/${roundId}/players/${playerId}/holes/${holeNumber}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...authHeaders,
                    },
                    body: JSON.stringify({ par }),
                }
            );
            if (response.status === 403) {
                throw new HttpError(response.status, `Par setting denied: ${response.status}`);
            }
            if (!response.ok) {
                throw new Error(`Failed to set hole par: ${response.status} ${response.statusText}`);
            }
            return {
                body: await response.json(),
            };
        } catch (error) {
            if (error instanceof HttpError) throw error;
            console.log(error);
            throw error;
        }
    }
}

export const roundService = new RoundService();
